import React, { useEffect, useRef, useState } from "react";
import { QrReader } from "react-qr-reader";

function BarcodeScanner({
    scanDetailsHandler,
}) {
    const [cameraError, setCameraError] = useState("");
    const [hasVideo, setHasVideo] = useState(false);
    const qrContainerRef = useRef(null);

    const onScanBarcode = (data) => {
        if (data) {
            scanDetailsHandler(data)
        }
    }

    const handleScannerError = (error) => {
        if (!error) return;

        // Ignore transient decode errors (e.g., no QR detected in frame)
        const errorName = error?.name || "";
        const errorMessage = error?.message || "";
        const isDecodeNoise = ["NotFoundException", "ChecksumException", "FormatException"].includes(errorName)
            || errorMessage.toLowerCase().includes("no qr code")
            || errorMessage.toLowerCase().includes("no code found");
        if (isDecodeNoise) return;

        // Map camera/permission issues to a user-friendly message
        if (["NotAllowedError", "SecurityError"].includes(errorName)) {
            setCameraError("Camera permission is blocked. Please allow access to start scanning.");
        } else if (["NotFoundError", "OverconstrainedError"].includes(errorName)) {
            setCameraError("No compatible camera found. Connect a camera and try again.");
        } else if (["NotReadableError", "TrackStartError"].includes(errorName)) {
            setCameraError("Camera is in use or unavailable. Close other apps using the camera.");
        } else {
            setCameraError("Unable to access the camera. Please check permissions and retry.");
        }
    };

    useEffect(() => {
        const container = qrContainerRef.current;
        if (!container) return;

        // Detect when the underlying video element starts playing to clear stale errors
        const video = container.querySelector("video");
        if (!video) return;

        const handleVideoStart = () => {
            setHasVideo(true);
            setCameraError("");
        };

        video.addEventListener("loadeddata", handleVideoStart);
        video.addEventListener("playing", handleVideoStart);

        return () => {
            video.removeEventListener("loadeddata", handleVideoStart);
            video.removeEventListener("playing", handleVideoStart);
        };
    }, []);

    return (
        <div
            ref={qrContainerRef}
            style={{
                width: "500px",
                height: "400px",
                marginTop: "-40px",
                position: "relative",
            }}
        >
            <QrReader
                constraints={{ facingMode: "environment" }}
                onResult={(result, error) => {
                    if (result) {
                        setCameraError("");
                        onScanBarcode(result?.text);
                    } else if (error) {
                        handleScannerError(error);
                    } else {
                        // Clear stale errors when the stream is active but no result yet
                        setCameraError("");
                    }
                }}
                containerStyle={{
                    width: "100%",
                    height: "100%",
                }}
                videoContainerStyle={{
                    paddingTop: "80%",
                }}
                videoStyle={{
                    width: "100%",
                    height: "100%",
                }}
            />
            {cameraError && !hasVideo && (
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(255, 255, 255, 0.88)",
                        color: "#333",
                        textAlign: "center",
                        padding: "16px",
                        borderRadius: "4px",
                        fontSize: "14px",
                        lineHeight: 1.4,
                    }}
                    aria-live="polite"
                >
                    {cameraError}
                </div>
            )}
        </div>
    );
}

export default BarcodeScanner;